import { useEffect, useId, useState } from 'react';
import type { Key } from 'react';
import type { Control, UseFormReturn, UseFormSetValue } from 'react-hook-form';
import { Controller, useWatch } from 'react-hook-form';
import {
  enableCountDistinctSimpleMetrics,
  enableEventLevelPercentileMetric,
  metricDataSourceValueColumnOptional,
  warehouseHostedMetricsSupportPercentiles,
  wneMetricConfigUnification,
} from '@gonfalon/dogfood-flags/dogfood-flags';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import {
  analysisTypeDisplayOptions,
  SNOWFLAKE_EXPERIMENTATION_INTEGRATION_KEY,
  successCriteriaDisplayOptions,
  VALID_ANALYSIS_PERCENTILES,
} from '@gonfalon/metrics/constants';
import { DEFAULT_EVENT_LEVEL_PERCENTILE, isValidEventLevelPercentile } from '@gonfalon/metrics/eventLevelPercentile';
import { getAnalysisTypeOption } from '@gonfalon/metrics/getAnalysisTypeOption';
import { isLDHosted } from '@gonfalon/metrics/isLDHosted';
import type { Metric, MetricDataSource } from '@gonfalon/metrics/types';
import { getMetricDataSourcesQuery } from '@gonfalon/rest-api/queries/metricDataSource';
import { useProjectKey } from '@gonfalon/router/useProjectKey';
import { Button, FieldGroup, ListBox, ListBoxItem, Popover, Select, SelectValue } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import { useQuery } from '@tanstack/react-query';

import { CodeSpan } from '../../../CodeSpan';
import { RequiredLabel } from '../../../CreateMetricModal/components/RequiredLabel';
import type { AggregationOption, AggregationOptionKey, MetricForm } from '../../../CreateMetricModal/formUtils';
import { getAggregationOptions } from '../../../CreateMetricModal/formUtils';
import { createAnalysisUnitsSelectHandler } from '../components/analysisUnitsSelection';
import { AnalysisUnitTokens } from '../components/AnalysisUnitTokens';
import { EventDefaultSelect } from '../components/EventDefaultPicker';
import { AnalysisUnitsError, MetricAnalysisUnits } from '../components/MetricAnalysisUnits';
import {
  ANALYSIS_TYPE_LABEL_ID,
  ANALYSIS_UNIT_LABEL_ID,
  SUCCESS_CRITERIA_LABEL_ID,
  UNIT_OF_MEASURE_LABEL_ID,
  UNITS_WITHOUT_EVENTS_LABEL_ID,
} from '../labels';
import { UnitFormField } from '../UnitFormField';

import { PercentileLevelField } from './PercentileLevelField';
import { SimpleMetricDefinitionSection } from './SimpleMetricDefinitionSection';

import styles from './MetricDefinitionFormField.module.css';

interface MetricDefinitionFormFieldProps {
  context: UseFormReturn<MetricForm>;
  /** Rows naming what to aggregate on, rendered first in the sectioned band's row grid. */
  leadingRows?: React.ReactNode;
  /**
   * Set when the parent renders `AggregateAsField` itself — the sectioned Simple layout puts it in
   * part 1, beneath the event key. The sentence then states the method instead of offering a picker.
   */
  externalAggregationField?: boolean;
  /** Renders the sectioned definition band instead of the flat sentence. Create modal only. */
  useSectionedLayout?: boolean;
  isEdit?: boolean;
}

const AGGREGATION_KEYS_BY_KIND: Partial<Record<NonNullable<Metric['kind']>, AggregationOptionKey[]>> = {
  pageview: ['count', 'count-distinct-units'],
  click: ['count', 'count-distinct-units'],
  // Traces support the full set (like custom) — we list the keys here only to
  // swap in span-specific descriptions.
  trace: ['count', 'count-distinct-units', 'sum', 'average'],
};

const aggregationDescriptionsByKind = (): Partial<
  Record<NonNullable<Metric['kind']>, Partial<Record<AggregationOptionKey, string>>>
> => ({
  pageview: {
    count: msg('metrics.metricDefinition.pageViewCountDescription'),
    'count-distinct-units': msg('metrics.metricDefinition.pageViewOccurrenceDescription'),
  },
  click: {
    count: msg('metrics.metricDefinition.clickCountDescription'),
    'count-distinct-units': msg('metrics.metricDefinition.clickOccurrenceDescription'),
  },
  trace: {
    count: msg('metrics.metricDefinition.traceCountDescription'),
    'count-distinct-units': msg('metrics.metricDefinition.traceOccurrenceDescription'),
    sum: msg('metrics.metricDefinition.traceSumDescription'),
    average: msg('metrics.metricDefinition.traceAverageDescription'),
  },
});

function getAggregationOptionsForKind(metricKind: Metric['kind']) {
  const aggregationOptions = getAggregationOptions();
  const allowedKeys = metricKind ? AGGREGATION_KEYS_BY_KIND[metricKind] : undefined;
  if (!allowedKeys) {
    return aggregationOptions;
  }
  const descriptions = metricKind ? aggregationDescriptionsByKind()[metricKind] : undefined;
  return aggregationOptions
    .filter((o) => allowedKeys.includes(o.key))
    .map((o) => ({ ...o, description: descriptions?.[o.key] ?? o.description }));
}

function getSentenceNouns(metricKind: Metric['kind']): {
  countNoun: string;
  valueNoun: string;
  occurrenceClause: string;
} {
  const kind = metricKind ?? 'custom';
  return {
    countNoun: msg('metrics.metricDefinition.countNoun', { kind }),
    valueNoun: msg('metrics.metricDefinition.valueNoun', { kind }),
    occurrenceClause: msg('metrics.metricDefinition.occurrenceClause', { kind }),
  };
}

function getAggregationKey(
  measureType: MetricForm['measureType'],
  unitAggregationType: MetricForm['unitAggregationType'],
  eventLevelPercentile?: boolean,
): AggregationOptionKey | null {
  if (eventLevelPercentile && measureType === 'value') {
    return 'event-percentile';
  }
  if (unitAggregationType === 'count_distinct') {
    return 'count_distinct';
  }
  if (measureType === 'count') {
    return 'count';
  }
  if (measureType === 'occurrence') {
    return 'count-distinct-units';
  }
  if (measureType === 'value') {
    if (unitAggregationType === 'sum') {
      return 'sum';
    }
    if (unitAggregationType === 'average') {
      return 'average';
    }
  }
  return null;
}

export const MetricDefinitionFormField: React.FC<MetricDefinitionFormFieldProps> = ({
  context,
  leadingRows,
  externalAggregationField = false,
  useSectionedLayout = false,
  isEdit = false,
}) => {
  const {
    control,
    formState: { errors },
    setValue,
  } = context;

  const selectedMeasureType = useWatch({ control, name: 'measureType', exact: true });
  const percentile = useWatch({ control, name: 'percentileValue', exact: true });
  const analysisType = useWatch({ control, name: 'analysisType', exact: true });
  const successCriteria = useWatch({
    control,
    name: 'successCriteria',
    exact: true,
  });
  const unitAggregationType = useWatch({ control, name: 'unitAggregationType', exact: true });
  const eventLevelPercentile = useWatch({ control, name: 'eventLevelPercentile', exact: true });
  const metricKind = useWatch({ control, name: 'kind', exact: true });
  const eventDefaultValue = useWatch({ control, name: 'eventDefault', exact: true });
  const countDistinctColumn = useWatch({ control, name: 'unitAggregationField', exact: true });
  const analysisUnits = useWatch({ control, name: 'analysisUnits', exact: true });
  const dataSourceKeyFormValue = useWatch({ control, name: 'dataSourceKey' });
  const warehousePercentilesEnabled = warehouseHostedMetricsSupportPercentiles();
  // LD-hosted metrics always support percentiles; warehouse-native sources support them
  // whenever the flag is on, regardless of warehouse type.
  const supportsPercentiles = isLDHosted(dataSourceKeyFormValue) || warehousePercentilesEnabled;
  const inlineEventLevelPercentileMode = useEventLevelPercentileMode(context, { canCreate: false, isEdit });

  useEffect(() => {
    if (!supportsPercentiles && analysisType === 'percentile' && !eventLevelPercentile) {
      setValue('analysisType', 'mean');
      setValue('percentileValue', undefined);
      setValue('eventDefault', { disabled: false, value: 0 });
    }
  }, [analysisType, eventLevelPercentile, setValue, supportsPercentiles]);

  const useAggregationDropdown =
    metricKind === 'custom' || metricKind === 'pageview' || metricKind === 'click' || metricKind === 'trace';

  if (!selectedMeasureType && !useAggregationDropdown) {
    return null;
  }

  // Aggregation errors are reported by whichever field renders the picker.
  const metricDefinitionError = [
    ...(externalAggregationField
      ? []
      : [errors.measureType?.message, errors.unitAggregationType?.message, errors.unitAggregationField?.message]),
    errors.analysisType?.message,
    errors.successCriteria?.message,
  ]
    .filter(Boolean)
    .join(', ');

  const analysisUnitsOnSelect = createAnalysisUnitsSelectHandler(context);

  const aggregationKey = getAggregationKey(selectedMeasureType, unitAggregationType, eventLevelPercentile);
  const isEventLevelPercentile = aggregationKey === 'event-percentile';

  const sentencePrefix = aggregationKey
    ? getAggregationOptions().find((o) => o.key === aggregationKey)?.sentencePrefix
    : null;

  const aggregationOptionsForKind = getAggregationOptionsForKind(metricKind);
  const sentenceNouns = getSentenceNouns(metricKind);

  const analysisUnitsForm = (
    <MetricAnalysisUnits
      initialValues={analysisUnits.map((au) => au.value)}
      onSelect={analysisUnitsOnSelect}
      dataSourceKey={dataSourceKeyFormValue}
      shouldFillWidth={useSectionedLayout}
      ariaLabelledBy={useSectionedLayout ? ANALYSIS_UNIT_LABEL_ID : undefined}
    />
  );

  // Page-view and click metrics have no success-criteria choice — the sentence states it.
  const successCriteriaControl =
    metricKind === 'custom' || metricKind === 'trace' ? (
      <SuccessCriteriaPicker
        currentSuccessCriteria={successCriteria}
        setValue={setValue}
        ariaLabelledBy={useSectionedLayout ? SUCCESS_CRITERIA_LABEL_ID : undefined}
      />
    ) : undefined;

  // Sectioned layout: the units echo into the sentence as read-only tokens and the picker
  // gets its own row. Flat layout: the picker sits in the sentence.
  const unitsSlot = useSectionedLayout ? (
    <AnalysisUnitTokens units={analysisUnits.map((au) => au.value)} />
  ) : (
    analysisUnitsForm
  );

  // In the sectioned layout the sentence is a read-only echo: every control it used to hold now
  // has its own labelled field, and the sentence states the resulting values in bold.
  // Lowercased for the prose: the sentence reads "then compute the average", while the picker
  // keeps the capitalised names a list of choices wants.
  const analysisTypeOption = getAnalysisTypeOption(analysisType, percentile);
  const analysisTypeDisplayName =
    analysisTypeDisplayOptions().find((option) => option.value === analysisTypeOption)?.displayName ??
    msg('metrics.metricDefinition.average');
  // `P95` is an acronym, so only the prose word (`Average`) is lowercased for the sentence.
  const analysisTypeLabel =
    typeof analysisTypeOption === 'number' ? analysisTypeDisplayName : analysisTypeDisplayName.toLowerCase();
  const successCriteriaLabel =
    successCriteriaDisplayOptions().find((option) => option.value === (successCriteria ?? 'HigherThanBaseline'))
      ?.displayName ?? msg('metrics.metricDefinition.higherIsBetter');

  const aggregationSlot =
    useSectionedLayout && externalAggregationField ? (
      <strong>{sentencePrefix}</strong>
    ) : (
      <AggregationMethodPicker
        control={control}
        aggregationKey={aggregationKey}
        sentencePrefix={sentencePrefix ?? null}
        options={aggregationOptionsForKind}
        setValue={setValue}
        clearErrors={context.clearErrors}
        eventLevelPercentileMode={inlineEventLevelPercentileMode}
      />
    );

  const analysisTypeSlot = useSectionedLayout ? (
    <strong>{analysisTypeLabel}</strong>
  ) : supportsPercentiles ? (
    <AnalysisTypePicker currentAnalysisType={analysisType} currentPercentileValue={percentile} setValue={setValue} />
  ) : (
    <span>
      <Message id="metrics.metricDefinition.average" />
    </span>
  );

  const countDistinctSlot = useSectionedLayout ? (
    <strong>{countDistinctColumn || msg('metrics.metricDefinition.columnNamePlaceholder')}</strong>
  ) : (
    <CountDistinctColumnPicker control={control} setValue={setValue} />
  );

  // `… where higher is better.` The flag-off layout puts the picker in that slot and keeps its
  // wording exactly; the sectioned layout states the value and moves the picker to its own row.
  // The slots are rich-text tags with no chunks: each renderer returns the control for that slot.
  const slotValues = {
    units: () => unitsSlot,
    column: () => countDistinctSlot,
    analysisType: () => analysisTypeSlot,
    criteria: () => successCriteriaControl ?? msg('metrics.metricDefinition.higherIsBetter'),
    clause: (chunks: React.ReactNode[]) => <span>{chunks}</span>,
    strong: (chunks: React.ReactNode[]) => <strong>{chunks}</strong>,
  };

  const sentence = isEventLevelPercentile ? (
    <Message
      id="metrics.metricDefinition.eventPercentileSentence"
      values={{
        percentile: () => (
          <CodeSpan
            textValue={
              isValidEventLevelPercentile(percentile)
                ? msg('metrics.definitionSummary.percentile', { value: percentile })
                : msg('metrics.metricDefinition.percentilePlaceholder')
            }
          />
        ),
        criteria: successCriteria ?? 'HigherThanBaseline',
      }}
    />
  ) : (
    <>
      {aggregationSlot}
      {aggregationKey &&
        sentencePrefix &&
        aggregationKey === 'count-distinct-units' &&
        (useSectionedLayout ? (
          <Message
            id="metrics.metricDefinition.occurrenceSentenceSectioned"
            values={{ ...slotValues, clauseText: sentenceNouns.occurrenceClause, criteriaText: successCriteriaLabel }}
          />
        ) : (
          <Message
            id="metrics.metricDefinition.occurrenceSentence"
            values={{ ...slotValues, clauseText: sentenceNouns.occurrenceClause }}
          />
        ))}
      {aggregationKey &&
        sentencePrefix &&
        aggregationKey === 'count_distinct' &&
        (useSectionedLayout ? (
          <Message
            id="metrics.metricDefinition.countDistinctSentenceSectioned"
            values={{ ...slotValues, criteriaText: successCriteriaLabel }}
          />
        ) : (
          <Message id="metrics.metricDefinition.countDistinctSentence" values={slotValues} />
        ))}
      {aggregationKey &&
        sentencePrefix &&
        aggregationKey !== 'count-distinct-units' &&
        aggregationKey !== 'count_distinct' &&
        (useSectionedLayout ? (
          <Message
            id="metrics.metricDefinition.valueSentenceSectioned"
            values={{
              ...slotValues,
              noun: aggregationKey === 'count' ? sentenceNouns.countNoun : sentenceNouns.valueNoun,
              criteriaText: successCriteriaLabel,
            }}
          />
        ) : (
          <Message
            id="metrics.metricDefinition.valueSentence"
            values={{
              ...slotValues,
              noun: aggregationKey === 'count' ? sentenceNouns.countNoun : sentenceNouns.valueNoun,
            }}
          />
        ))}
    </>
  );

  const analysisUnitsError = (
    <AnalysisUnitsError
      message={errors.analysisUnits?.message}
      dataSourceKey={dataSourceKeyFormValue}
      useSectionedLayout={useSectionedLayout}
    />
  );

  if (useSectionedLayout) {
    // Value-type rows only, and only for the kinds that render them in the flat layout — there
    // they live in CustomMetricKindDetails, so a click or page view metric never sees them. The
    // aggregation is not reset when Event kind changes, so without the kind check switching from
    // Custom/Sum to Clicked leaves a required `unit` field on a metric that has no use for it.
    const supportsValueRows = metricKind === 'custom' || metricKind === 'trace';
    const showValueRows =
      supportsValueRows && selectedMeasureType === 'value' && unitAggregationType !== 'count_distinct';
    const hideEventDefault = analysisType === 'percentile';
    const unitsWithoutEvents: 'included' | 'excluded' = eventDefaultValue?.disabled ? 'excluded' : 'included';

    return (
      <SimpleMetricDefinitionSection
        id="metricDefinition"
        errorMessage={metricDefinitionError}
        leadingRows={leadingRows}
        sentence={sentence}
        // The flat sentence offers this picker for every aggregation except the occurrence one,
        // which has no per-unit values to roll up. Showing it there would let a percentile be set
        // on an occurrence metric — a combination the flat layout cannot produce.
        analysisTypeControl={
          supportsPercentiles &&
          aggregationKey &&
          aggregationKey !== 'count-distinct-units' &&
          !isEventLevelPercentile ? (
            <AnalysisTypeField context={context} />
          ) : undefined
        }
        percentileLevelControl={isEventLevelPercentile ? <PercentileLevelField context={context} /> : undefined}
        analysisUnitControl={isEventLevelPercentile ? undefined : analysisUnitsForm}
        analysisUnitError={isEventLevelPercentile ? undefined : analysisUnitsError}
        successCriteriaControl={successCriteriaControl}
        unitsWithoutEventsControl={
          showValueRows && !hideEventDefault ? (
            <FieldGroup errorMessage={errors.eventDefault?.message}>
              <EventDefaultSelect
                unitsWithoutEvents={unitsWithoutEvents}
                setValue={setValue}
                className={styles.sectionSelect}
                ariaLabelledBy={UNITS_WITHOUT_EVENTS_LABEL_ID}
              />
            </FieldGroup>
          ) : undefined
        }
        unitOfMeasureControl={
          showValueRows ? <UnitFormField context={context} aria-labelledby={UNIT_OF_MEASURE_LABEL_ID} /> : undefined
        }
      />
    );
  }

  return (
    <FieldGroup errorMessage={metricDefinitionError}>
      <RequiredLabel label={msg('metrics.metricDefinition.definitionLabel')} htmlFor="metricDefinition" />
      <span className={styles.metricDefinition} id="metricDefinition">
        {sentence}
      </span>
      {analysisUnitsError}
    </FieldGroup>
  );
};

/**
 * "Aggregate as" — the aggregation method, pulled out of the definition sentence so the sentence
 * can read as static prose. Owns the `measureType` / `unitAggregationType` Controllers, so it must
 * render exactly once: the sectioned Simple layout hoists it into part 1, beneath the event key.
 *
 * Also carries the count-distinct column picker, which only means anything next to the method that
 * asks for it — the same pairing the ratio term cards use.
 */
export const AggregateAsField: React.FC<{ context: UseFormReturn<MetricForm>; isEdit?: boolean }> = ({
  context,
  isEdit = false,
}) => {
  const {
    control,
    setValue,
    clearErrors,
    formState: { errors },
  } = context;
  const aggregateAsLabelId = useId();
  const countDistinctLabelId = useId();
  const selectedMeasureType = useWatch({ control, name: 'measureType', exact: true });
  const unitAggregationType = useWatch({ control, name: 'unitAggregationType', exact: true });
  const eventLevelPercentile = useWatch({ control, name: 'eventLevelPercentile', exact: true });
  const metricKind = useWatch({ control, name: 'kind', exact: true });

  const eventLevelPercentileMode = useEventLevelPercentileMode(context, { canCreate: true, isEdit });
  const aggregationKey = getAggregationKey(selectedMeasureType, unitAggregationType, eventLevelPercentile);
  const sentencePrefix = aggregationKey
    ? getAggregationOptions().find((o) => o.key === aggregationKey)?.sentencePrefix
    : null;

  return (
    <div className={styles.aggregateAsFields}>
      <FieldGroup
        className={styles.labelledField}
        errorMessage={[errors.measureType?.message, errors.unitAggregationType?.message].filter(Boolean).join(', ')}
      >
        <RequiredLabel label={msg('metrics.metricDefinition.aggregateAsLabel')} id={aggregateAsLabelId} />
        <AggregationMethodPicker
          control={control}
          aggregationKey={aggregationKey}
          sentencePrefix={sentencePrefix ?? null}
          options={getAggregationOptionsForKind(metricKind)}
          setValue={setValue}
          clearErrors={clearErrors}
          ariaLabelledBy={aggregateAsLabelId}
          eventLevelPercentileMode={eventLevelPercentileMode}
        />
      </FieldGroup>
      {aggregationKey === 'count_distinct' && (
        <FieldGroup className={styles.labelledField} errorMessage={errors.unitAggregationField?.message}>
          <RequiredLabel label={msg('metrics.metricDefinition.countDistinctOnLabel')} id={countDistinctLabelId} />
          <CountDistinctColumnPicker control={control} setValue={setValue} ariaLabelledBy={countDistinctLabelId} />
        </FieldGroup>
      )}
    </div>
  );
};

/**
 * "Analysis type" — how per-unit values roll up across units (Average, P95, …). Rendered only when
 * the data source supports percentiles; otherwise it is always Average and there is nothing to pick.
 */
export const AnalysisTypeField: React.FC<{ context: UseFormReturn<MetricForm> }> = ({ context }) => {
  const { control, setValue } = context;
  const analysisType = useWatch({ control, name: 'analysisType', exact: true });
  const percentile = useWatch({ control, name: 'percentileValue', exact: true });

  return (
    <AnalysisTypePicker
      currentAnalysisType={analysisType}
      currentPercentileValue={percentile}
      setValue={setValue}
      ariaLabelledBy={ANALYSIS_TYPE_LABEL_ID}
    />
  );
};

interface AggregationMethodPickerProps {
  control: Control<MetricForm>;
  aggregationKey: AggregationOptionKey | null;
  sentencePrefix: string | null;
  options: AggregationOption[];
  setValue: UseFormSetValue<MetricForm>;
  clearErrors: UseFormReturn<MetricForm>['clearErrors'];
  /** Set when a row's visible label names this picker, replacing its own `aria-label`. */
  ariaLabelledBy?: string;
  eventLevelPercentileMode?: EventLevelPercentileMode;
}

type EventLevelPercentileMode = 'hidden' | 'available' | 'locked';

const useIsSnowflakeExperimentationSource = (control: Control<MetricForm>, isEnabled: boolean): boolean => {
  const projectKey = useProjectKey();
  const dataSourceKey = useWatch({ control, name: 'dataSourceKey' });
  const isWarehouseSource = Boolean(dataSourceKey) && !isLDHosted(dataSourceKey);
  const { data } = useQuery({
    ...getMetricDataSourcesQuery({ projKey: projectKey }),
    enabled: isEnabled && isWarehouseSource,
  });
  if (!isEnabled || !isWarehouseSource) {
    return false;
  }
  return (
    data?.items?.find((ds) => ds.key === dataSourceKey)?.integrationKey === SNOWFLAKE_EXPERIMENTATION_INTEGRATION_KEY
  );
};

const useEventLevelPercentileMode = (
  context: UseFormReturn<MetricForm>,
  { canCreate, isEdit }: { canCreate: boolean; isEdit: boolean },
): EventLevelPercentileMode => {
  const { control, formState } = context;
  const [persisted] = useState(() => ({
    isEventLevelPercentile: Boolean(formState.defaultValues?.eventLevelPercentile),
  }));
  const metricKind = useWatch({ control, name: 'kind', exact: true });
  const ratioOrSimple = useWatch({ control, name: 'ratioOrSimple', exact: true });
  const isFlagOn = enableEventLevelPercentileMetric();
  const warehousePercentilesOn = warehouseHostedMetricsSupportPercentiles();
  const isSnowflakeSource = useIsSnowflakeExperimentationSource(control, canCreate && isFlagOn && !isEdit);

  if (isEdit) {
    return persisted.isEventLevelPercentile ? 'locked' : 'hidden';
  }
  if (
    !canCreate ||
    !isFlagOn ||
    !warehousePercentilesOn ||
    metricKind !== 'custom' ||
    ratioOrSimple === 'ratio' ||
    !isSnowflakeSource
  ) {
    return 'hidden';
  }
  return 'available';
};

type DataSourceValueColumnInfo = Pick<MetricDataSource, 'key'> & {
  columnMappings?: Pick<NonNullable<MetricDataSource['columnMappings']>, 'valueColumn'>;
};

// MTRX-2050: when the selected data source has no `valueColumn` mapping,
// numeric metric kinds (Sum / Average) can't produce a meaningful query, so
// hide those options from the picker. The DS-level flag governs whether the
// underlying scenario is even possible.
export function dataSourceHidesNumericOptions(
  dataSources: DataSourceValueColumnInfo[] | undefined,
  dataSourceKey?: string,
): boolean {
  if (
    !metricDataSourceValueColumnOptional() ||
    wneMetricConfigUnification() ||
    !dataSourceKey ||
    isLDHosted(dataSourceKey)
  ) {
    return false;
  }
  const selectedDataSource = dataSources?.find((ds) => ds.key === dataSourceKey);
  if (!selectedDataSource) {
    return false;
  }
  const valueColumn = selectedDataSource.columnMappings?.valueColumn;
  return !valueColumn || valueColumn.trim().length === 0;
}

const useHideNumericOptions = (control: Control<MetricForm>): boolean => {
  const projectKey = useProjectKey();
  const dataSourceKey = useWatch({ control, name: 'dataSourceKey' });
  const flagOn = metricDataSourceValueColumnOptional();
  const configUnificationOn = wneMetricConfigUnification();
  const { data } = useQuery({
    ...getMetricDataSourcesQuery({ projKey: projectKey }),
    enabled: flagOn && !configUnificationOn && Boolean(dataSourceKey) && !isLDHosted(dataSourceKey),
  });
  return dataSourceHidesNumericOptions(data?.items, dataSourceKey);
};

// Target-column count_distinct is warehouse-native only, gated behind the flag.
const useShowSimpleCountDistinctOption = (control: Control<MetricForm>): boolean => {
  const dataSourceKey = useWatch({ control, name: 'dataSourceKey' });
  const flagOn = enableCountDistinctSimpleMetrics();
  return flagOn && Boolean(dataSourceKey) && !isLDHosted(dataSourceKey);
};

const AggregationMethodPicker: React.FC<AggregationMethodPickerProps> = ({
  control,
  aggregationKey,
  sentencePrefix,
  options,
  setValue,
  clearErrors,
  ariaLabelledBy,
  eventLevelPercentileMode = 'hidden',
}) => {
  const hideNumericOptions = useHideNumericOptions(control);
  const showCountDistinct = useShowSimpleCountDistinctOption(control);
  const eventLevelPercentile = useWatch({ control, name: 'eventLevelPercentile', exact: true });
  const showEventLevelPercentile =
    eventLevelPercentileMode === 'locked' || (eventLevelPercentileMode === 'available' && !hideNumericOptions);
  let visibleOptions = hideNumericOptions
    ? options.filter((option) => option.key !== 'sum' && option.key !== 'average')
    : options;
  if (!showCountDistinct) {
    visibleOptions = visibleOptions.filter((option) => option.key !== 'count_distinct');
  }
  if (!showEventLevelPercentile) {
    visibleOptions = visibleOptions.filter((option) => option.key !== 'event-percentile');
  }

  function handleSelect(key: Key | null) {
    // Defense in depth: Sum/Average are hidden from the listbox when the data
    // source has no value column, but guard against a programmatic value change
    // (e.g. reopening a numeric metric in the edit flow) reaching this path.
    if (hideNumericOptions && (key === 'sum' || key === 'average')) {
      return;
    }
    if (key === 'count_distinct' && !showCountDistinct) {
      return;
    }
    if (key === 'event-percentile') {
      if (eventLevelPercentileMode !== 'available' || hideNumericOptions || eventLevelPercentile) {
        return;
      }
      setValue('eventLevelPercentile', true, { shouldDirty: true });
      setValue('measureType', 'value', { shouldDirty: true, shouldValidate: true });
      setValue('unitAggregationType', 'average', { shouldDirty: true, shouldValidate: true });
      setValue('isNumeric', true, { shouldDirty: true });
      setValue('analysisType', 'percentile', { shouldDirty: true });
      setValue('percentileValue', DEFAULT_EVENT_LEVEL_PERCENTILE, { shouldDirty: true });
      setValue('eventDefault', { disabled: true }, { shouldDirty: true });
      setValue('successCriteria', 'LowerThanBaseline', { shouldDirty: true });
      setValue('winsorization', undefined, { shouldDirty: true });
      setValue('unitAggregationField', undefined, { shouldDirty: true });
      clearErrors(['measureType', 'unitAggregationType', 'analysisUnits']);
      return;
    }
    if (
      eventLevelPercentile &&
      (key === 'count' ||
        key === 'count-distinct-units' ||
        key === 'sum' ||
        key === 'average' ||
        key === 'count_distinct')
    ) {
      setValue('eventLevelPercentile', false, { shouldDirty: true });
      setValue('successCriteria', 'HigherThanBaseline', { shouldDirty: true });
    }
    if (key === 'count') {
      setValue('measureType', 'count', { shouldDirty: true, shouldValidate: true });
      setValue('eventDefault', { disabled: false, value: 0 }, { shouldDirty: true });
      setValue('unit', undefined);
      setValue('analysisType', 'mean', { shouldDirty: true });
      setValue('percentileValue', undefined);
      setValue('unitAggregationType', 'sum', { shouldDirty: true, shouldValidate: true });
      setValue('isNumeric', false, { shouldDirty: true });
    } else if (key === 'count-distinct-units') {
      setValue('measureType', 'occurrence', { shouldDirty: true, shouldValidate: true });
      setValue('eventDefault', { disabled: false, value: 0 }, { shouldDirty: true });
      setValue('unit', undefined);
      setValue('analysisType', 'mean', { shouldDirty: true });
      setValue('percentileValue', undefined);
      setValue('unitAggregationType', 'average', { shouldDirty: true, shouldValidate: true });
      setValue('isNumeric', false, { shouldDirty: true });
    } else if (key === 'sum') {
      setValue('measureType', 'value', { shouldDirty: true, shouldValidate: true });
      setValue('eventDefault', { disabled: false, value: 0 }, { shouldDirty: true });
      setValue('analysisType', 'mean', { shouldDirty: true });
      setValue('percentileValue', undefined);
      setValue('unitAggregationType', 'sum', { shouldDirty: true, shouldValidate: true });
      setValue('isNumeric', true, { shouldDirty: true });
    } else if (key === 'average') {
      setValue('measureType', 'value', { shouldDirty: true, shouldValidate: true });
      setValue('eventDefault', { disabled: false, value: 0 }, { shouldDirty: true });
      setValue('analysisType', 'mean', { shouldDirty: true });
      setValue('percentileValue', undefined);
      setValue('unitAggregationType', 'average', { shouldDirty: true, shouldValidate: true });
      setValue('isNumeric', true, { shouldDirty: true });
    } else if (key === 'count_distinct') {
      setValue('measureType', 'value', { shouldDirty: true, shouldValidate: true });
      setValue('eventDefault', { disabled: false, value: 0 }, { shouldDirty: true });
      setValue('unit', undefined);
      setValue('analysisType', 'mean', { shouldDirty: true });
      setValue('percentileValue', undefined);
      setValue('unitAggregationType', 'count_distinct', {
        shouldDirty: true,
        shouldValidate: true,
      });
      // Per-unit distinct counts are numeric values, so the statistical analysis
      // must model them with a numeric distribution.
      setValue('isNumeric', true, { shouldDirty: true });
      // count_distinct is incompatible with winsorization.
      setValue('winsorization', undefined, { shouldDirty: true });
      clearErrors(['measureType', 'unitAggregationType']);
      return;
    } else {
      return;
    }
    setValue('unitAggregationField', undefined, { shouldDirty: true });
    clearErrors(['measureType', 'unitAggregationType']);
  }

  return (
    <>
      <Controller
        control={control}
        name="measureType"
        rules={{ required: msg('metrics.metricDefinition.selectionRequired') }}
        render={() => (
          <Select
            onChange={handleSelect}
            value={aggregationKey}
            isDisabled={eventLevelPercentileMode === 'locked'}
            aria-label={ariaLabelledBy ? undefined : msg('metrics.metricDefinition.aggregationMethodLabel')}
            aria-labelledby={ariaLabelledBy}
            placeholder={msg('metrics.metricDefinition.aggregationPlaceholder')}
            className={styles.aggregationMethodSelect}
          >
            <Button data-test-id="aggregation-method-select">
              <SelectValue>
                {({ isPlaceholder }) =>
                  isPlaceholder ? msg('metrics.metricDefinition.selectPlaceholder') : (sentencePrefix ?? '')
                }
              </SelectValue>
              <Icon name="chevron-down" size="small" slot="end-icon" />
            </Button>
            <Popover className={styles.aggregationMethodPopover}>
              <ListBox items={visibleOptions}>
                {(item) => (
                  <ListBoxItem id={item.key} textValue={item.name} aria-label={item.name}>
                    <div className={styles.aggregationOptionLabel}>
                      <span className={styles.aggregationOptionName}>{item.name}</span>
                      <span className={styles.aggregationOptionDescription}>{item.description}</span>
                    </div>
                  </ListBoxItem>
                )}
              </ListBox>
            </Popover>
          </Select>
        )}
      />
      <Controller
        control={control}
        name="unitAggregationType"
        rules={{
          validate: (value, formValues) =>
            formValues.measureType === 'value'
              ? !!value || msg('metrics.metricDefinition.valueAggregationRequired')
              : true,
        }}
        render={() => <></>}
      />
    </>
  );
};

interface AnalysisTypePickerProps {
  currentAnalysisType?: Metric['analysisType'];
  currentPercentileValue?: number;
  setValue: UseFormSetValue<MetricForm>;
  /** Set when a row's visible label names this picker, replacing its own `aria-label`. */
  ariaLabelledBy?: string;
}
const AnalysisTypePicker: React.FC<AnalysisTypePickerProps> = (props: AnalysisTypePickerProps) => {
  const selectedKey = getAnalysisTypeOption(props.currentAnalysisType, props.currentPercentileValue);
  function handleAnalysisTypeChange(key: Key | null) {
    if (Number.isInteger(key) && VALID_ANALYSIS_PERCENTILES.includes(Number(key))) {
      props.setValue('analysisType', 'percentile', { shouldDirty: true });
      // We need to update eventDefault to be disabled here - it is not supported for percentile analysis
      props.setValue('eventDefault', { disabled: true }, { shouldDirty: true });
      props.setValue('percentileValue', Number(key), { shouldDirty: true });
    } else {
      props.setValue('eventDefault', { disabled: false, value: 0 }, { shouldDirty: true });
      props.setValue('analysisType', 'mean', { shouldDirty: true });
      props.setValue('percentileValue', undefined, { shouldDirty: true });
    }
  }
  return (
    <Select
      onChange={handleAnalysisTypeChange}
      value={selectedKey}
      aria-label={props.ariaLabelledBy ? undefined : msg('metrics.metricDefinition.analysisTypePickerLabel')}
      aria-labelledby={props.ariaLabelledBy}
      className={styles.analysisTypeSelect}
    >
      <Button>
        <SelectValue />
        <Icon name="chevron-down" size="small" slot="end-icon" />
      </Button>
      <Popover className={styles.popover}>
        <ListBox>
          {analysisTypeDisplayOptions().map((option) => (
            <ListBoxItem key={option.value} id={option.value}>
              {option.displayName}
            </ListBoxItem>
          ))}
        </ListBox>
      </Popover>
    </Select>
  );
};

interface SuccessCriteriaPickerProps {
  currentSuccessCriteria?: string;
  setValue: UseFormSetValue<MetricForm>;
  /** Set when a row's visible label names this picker, replacing its own `aria-label`. */
  ariaLabelledBy?: string;
}
const SuccessCriteriaPicker: React.FC<SuccessCriteriaPickerProps> = (props: SuccessCriteriaPickerProps) => {
  function handleSuccessCriteriaChange(key: Key | null) {
    if (key === 'HigherThanBaseline' || key === 'LowerThanBaseline') {
      props.setValue('successCriteria', key, { shouldDirty: true });
    }
  }
  return (
    <Select
      onChange={handleSuccessCriteriaChange}
      value={props.currentSuccessCriteria ?? 'HigherThanBaseline'}
      aria-label={props.ariaLabelledBy ? undefined : msg('metrics.metricDefinition.successCriteriaPickerLabel')}
      aria-labelledby={props.ariaLabelledBy}
      className={styles.sectionSelect}
    >
      <Button data-test-id="success-criteria-button">
        <SelectValue />
        <Icon name="chevron-down" size="small" slot="end-icon" />
      </Button>
      <Popover className={styles.popover}>
        <ListBox>
          {successCriteriaDisplayOptions().map((option) => (
            <ListBoxItem key={option.value} id={option.value}>
              {option.displayName}
            </ListBoxItem>
          ))}
        </ListBox>
      </Popover>
    </Select>
  );
};

interface CountDistinctColumnPickerProps {
  control: Control<MetricForm>;
  setValue: UseFormSetValue<MetricForm>;
  /** Set when a row's visible label names this picker, replacing its own `aria-label`. */
  ariaLabelledBy?: string;
}
const CountDistinctColumnPicker: React.FC<CountDistinctColumnPickerProps> = ({ control, setValue, ariaLabelledBy }) => {
  const projectKey = useProjectKey();
  const dataSourceKey = useWatch({ control, name: 'dataSourceKey' });
  const selectedColumn = useWatch({ control, name: 'unitAggregationField' });
  const { data } = useQuery({
    ...getMetricDataSourcesQuery({ projKey: projectKey }),
    enabled: Boolean(dataSourceKey) && !isLDHosted(dataSourceKey),
  });
  const columns = data?.items?.find((ds) => ds.key === dataSourceKey)?.columnMappings?.columns ?? [];

  return (
    <Controller
      control={control}
      name="unitAggregationField"
      rules={{ required: msg('metrics.metricDefinition.columnRequired') }}
      // Unregister on unmount so switching away from Count distinct drops the
      // `required` rule; otherwise `isValid` stays false with no visible field to fix.
      shouldUnregister
      render={() => (
        <Select
          aria-label={ariaLabelledBy ? undefined : msg('metrics.metricDefinition.countDistinctOnLabel')}
          aria-labelledby={ariaLabelledBy}
          selectedKey={selectedColumn || null}
          onSelectionChange={(key) => {
            if (typeof key === 'string') {
              setValue('unitAggregationField', key, { shouldDirty: true, shouldValidate: true });
            }
          }}
          isDisabled={columns.length === 0}
          placeholder={msg('metrics.metricDefinition.columnPlaceholder')}
          className={styles.sectionSelect}
        >
          <Button data-test-id="count-distinct-column-select">
            <SelectValue />
            <Icon name="chevron-down" size="small" slot="end-icon" />
          </Button>
          <Popover className={styles.countDistinctPopover}>
            <ListBox items={columns}>{(col) => <ListBoxItem id={col.name}>{col.name}</ListBoxItem>}</ListBox>
          </Popover>
        </Select>
      )}
    />
  );
};
